'use client';
import React, { useRef, useEffect, useState, useCallback } from 'react';
import ReactDOM from 'react-dom';
import styled from '@emotion/styled';
import { css } from '@emotion/react';
import { Button } from '@edusense/shared-ui-kit';
import { LAYOUT_BREAKPOINTS } from '@edusense/shared-constants';
/**
 * 기본 모달 컴포넌트
 * 단순 UI 기능만 제공하는 재사용 가능한 모달
 */ export default function Modal({ isOpen, onClose, title, children, width = 'md', forceMobileFull = true, position = 'center', showBackdrop = true, noPadding = false, disableMobileFullscreen = false, isDraggable = false, opacity = 1, enableOpacityControl = false, contentStyle }) {
    const modalRef = useRef(null);
    const headerRef = useRef(null);
    const [isMobile, setIsMobile] = useState(false);
    const [modalRoot, setModalRoot] = useState(null);
    // --- Draggable State ---
    const [isDragging, setIsDragging] = useState(false);
    const [offset, setOffset] = useState({
        x: 0,
        y: 0
    });
    const dragStartPos = useRef({
        x: 0,
        y: 0
    });
    const initialOffset = useRef({
        x: 0,
        y: 0
    });
    // --- End Draggable State ---
    // --- Opacity State ---
    const [currentOpacity, setCurrentOpacity] = useState(opacity); // Internal state for slider
    // Reset internal opacity state based on props when modal opens/closes or control is toggled
    useEffect(()=>{
        const validInitialOpacity = Math.max(0, Math.min(1, opacity));
        if (isOpen) {
            // When opening, reset to prop value only if control is disabled OR it's the initial load
            // This allows the user's slider setting to persist while open if control is enabled
            if (!enableOpacityControl || currentOpacity === undefined) {
                setCurrentOpacity(validInitialOpacity);
            }
        }
        // If control is disabled, always reflect the prop value
        if (!enableOpacityControl) {
            setCurrentOpacity(validInitialOpacity);
        }
    }, [
        isOpen,
        enableOpacityControl,
        opacity
    ]); // Rerun when these change
    // Update internal state when slider changes
    const handleOpacityChange = (event)=>{
        setCurrentOpacity(parseFloat(event.target.value));
    };
    // --- End Opacity State ---
    useEffect(()=>{
        const element = document.getElementById('modal-root');
        console.log('Finding #modal-root:', element);
        setModalRoot(element);
        const checkIsMobile = ()=>{
            setIsMobile(window.innerWidth < LAYOUT_BREAKPOINTS.TABLET_MIN);
        };
        checkIsMobile();
        window.addEventListener('resize', checkIsMobile);
        return ()=>{
            window.removeEventListener('resize', checkIsMobile);
        };
    }, []);
    const isFullscreenMobile = isMobile && !disableMobileFullscreen && (forceMobileFull || width === 'full');
    // Reset offset when modal opens
    useEffect(()=>{
        if (isOpen) {
            setOffset({
                x: 0,
                y: 0
            });
        }
    }, [
        isOpen
    ]);
    // --- Event Handlers for Dragging ---
    const handleMouseMove = useCallback((event)=>{
        if (!modalRef.current) return;
        const deltaX = event.clientX - dragStartPos.current.x;
        const deltaY = event.clientY - dragStartPos.current.y;
        const newX = initialOffset.current.x + deltaX;
        const newY = initialOffset.current.y + deltaY;
        modalRef.current.style.transform = `translate(${newX}px, ${newY}px)`;
    }, []);
    const handleMouseUp = useCallback((event)=>{
        if (!modalRef.current) return;
        setIsDragging(false);
        const deltaX = event.clientX - dragStartPos.current.x;
        const deltaY = event.clientY - dragStartPos.current.y;
        setOffset({
            x: initialOffset.current.x + deltaX,
            y: initialOffset.current.y + deltaY
        });
        document.removeEventListener('mousemove', handleMouseMove);
        document.removeEventListener('mouseup', handleMouseUp);
    }, [
        handleMouseMove
    ]);
    const handleMouseDown = useCallback((event)=>{
        if (!isDraggable || isFullscreenMobile) return;
        if (!modalRef.current) return;
        if (event.target.closest('button')) {
            return;
        }
        event.preventDefault();
        setIsDragging(true);
        dragStartPos.current = {
            x: event.clientX,
            y: event.clientY
        };
        initialOffset.current = {
            ...offset
        };
        document.addEventListener('mousemove', handleMouseMove);
        document.addEventListener('mouseup', handleMouseUp);
    }, [
        offset,
        isDraggable,
        isFullscreenMobile,
        handleMouseMove,
        handleMouseUp
    ]);
    // --- End Event Handlers ---
    const handleClickOutside = useCallback((event)=>{
        if (isDragging) return;
        if (isOpen && modalRef.current && !modalRef.current.contains(event.target)) {
            onClose();
        }
    }, [
        isOpen,
        onClose,
        isDragging
    ]);
    useEffect(()=>{
        if (isOpen && showBackdrop && !isFullscreenMobile) {
            document.addEventListener('mousedown', handleClickOutside);
        } else {
            document.removeEventListener('mousedown', handleClickOutside);
        }
        return ()=>document.removeEventListener('mousedown', handleClickOutside);
    }, [
        isOpen,
        showBackdrop,
        isFullscreenMobile,
        handleClickOutside
    ]);
    useEffect(()=>{
        document.body.style.overflow = isOpen ? 'hidden' : '';
        return ()=>{
            document.body.style.overflow = '';
        };
    }, [
        isOpen
    ]);
    useEffect(()=>{
        const handleEscKey = (event)=>{
            if (event.key === 'Escape') onClose();
        };
        if (isOpen) document.addEventListener('keydown', handleEscKey);
        else document.removeEventListener('keydown', handleEscKey);
        return ()=>document.removeEventListener('keydown', handleEscKey);
    }, [
        isOpen,
        onClose
    ]);
    if (!modalRoot) {
        console.log('Modal rendering skipped, modalRoot not found yet:', modalRoot);
        return null; // Still need this check for portal target
    }
    const CloseIcon = ()=>/*#__PURE__*/ React.createElement("svg", {
            width: "20",
            height: "20",
            fill: "none",
            viewBox: "0 0 24 24",
            stroke: "currentColor"
        }, /*#__PURE__*/ React.createElement("path", {
            strokeLinecap: "round",
            strokeLinejoin: "round",
            strokeWidth: 2,
            d: "M6 18L18 6M6 6l12 12"
        }));
    // Use internal currentOpacity state for styling
    const displayOpacity = enableOpacityControl ? currentOpacity : Math.max(0, Math.min(1, opacity));
    // Always render the portal, control visibility via ModalContainer styles
    return /*#__PURE__*/ ReactDOM.createPortal(/*#__PURE__*/ React.createElement(ModalContainer, {
        isOpen: isOpen,
        isFullscreenMobile: isFullscreenMobile,
        position: position,
        showBackdrop: showBackdrop
    }, /*#__PURE__*/ React.createElement(ModalContent, {
        ref: modalRef,
        isFullscreenMobile: isFullscreenMobile,
        width: width,
        position: position,
        onClick: (e)=>e.stopPropagation(),
        style: {
            transform: `translate(${offset.x}px, ${offset.y}px)`,
            transition: isDragging ? 'none' : 'transform 0s ease',
            ...contentStyle || {}
        },
        opacityValue: displayOpacity,
        opacityControlEnabled: true
    }, title && /*#__PURE__*/ React.createElement(ModalHeader, {
        ref: headerRef,
        onMouseDown: handleMouseDown,
        isDraggable: isDraggable && !isFullscreenMobile
    }, /*#__PURE__*/ React.createElement(HeaderSection, null, /*#__PURE__*/ React.createElement(Button, {
        iconOnly: true,
        icon: /*#__PURE__*/ React.createElement(CloseIcon, null),
        variant: "ghost",
        size: "xs",
        onClick: onClose,
        label: "닫기"
    })), /*#__PURE__*/ React.createElement(HeaderSection, {
        center: true
    }, /*#__PURE__*/ React.createElement(ModalTitle, null, title), enableOpacityControl && !isFullscreenMobile && /*#__PURE__*/ React.createElement(OpacitySliderContainer, null, /*#__PURE__*/ React.createElement("input", {
        type: "range",
        min: "0.1",
        max: "1",
        step: "0.05",
        value: currentOpacity,
        onChange: handleOpacityChange,
        onMouseDown: (e)=>e.stopPropagation()
    }))), /*#__PURE__*/ React.createElement(HeaderSection, null)), /*#__PURE__*/ React.createElement(ModalBody, {
        isFullscreenMobile: isFullscreenMobile,
        noPadding: noPadding
    }, children))), modalRoot);
}
// Modify ModalContainer styled component
const ModalContainer = styled.div`
  position: fixed;
  inset: 0;
  z-index: 9999;
  display: flex;

  /* --- CSS Visibility Control --- */
  visibility: ${(props)=>props.isOpen ? 'visible' : 'hidden'};
  opacity: ${(props)=>props.isOpen ? 1 : 0};
  transition: visibility 0s linear ${(props)=>props.isOpen ? '0s' : '0.3s'}, opacity 0.3s ease;

  /* --- Pointer Events Control --- */
  pointer-events: ${(props)=>props.isOpen && props.showBackdrop ? 'auto' : 'none'};

  /* Apply backdrop only when open and needed */
  ${(props)=>props.showBackdrop && props.isOpen && css`
    background-color: rgba(0, 0, 0, 0.5);
    backdrop-filter: blur(2px);
  `}

  /* --- Positioning Logic --- */
  ${(props)=>{
    const alignmentStyles = css`
        align-items: center;
        justify-content: center;
      `; // Default center alignment
    if (props.position === 'bottom-left') {
        return css`
            align-items: flex-end;
            justify-content: flex-start;
        `;
    } else if (props.position === 'bottom-right') {
        return css`
            align-items: flex-end;
            justify-content: flex-end;
        `;
    }
    // Only apply default center alignment if not bottom-left or bottom-right
    return alignmentStyles;
}}

  /* Apply positioning consistently across screen sizes when not fullscreen mobile */
  ${(props)=>!props.isFullscreenMobile && css`
    // Apply the same logic derived above directly
    ${(()=>{
        if (props.position === 'bottom-left') {
            return css` align-items: flex-end; justify-content: flex-start; `;
        } else if (props.position === 'bottom-right') {
            return css` align-items: flex-end; justify-content: flex-end; `;
        }
        // Default center only if not bottom-left/right
        return css` align-items: center; justify-content: center; `;
    })()}
  `}

  /* Override for fullscreen mobile if needed (might not be necessary anymore) */
  ${(props)=>props.isFullscreenMobile && css`
      align-items: stretch; /* Or initial? */
      justify-content: stretch; /* Or initial? */
      /* Fullscreen mobile specific alignment overrides can go here */
  `}
`;
const ModalContent = styled.div`
  /* 배경색: 기본은 my-post-background, 다크/vscode 모드에서는 my-card-background 사용 */
  background-color: var(--my-post-background);
  html.dark &, body.vscode-dark & {
      background-color: var(--my-card-background);
  }
  border: 1px solid var(--theme-border);
  box-shadow: var(--theme-shadow);
  overflow: auto;
  pointer-events: auto;
  position: relative;

  /* Apply opacity value directly - controlled by state/props */
  opacity: ${(props)=>props.opacityValue};

  ${(props)=>props.isFullscreenMobile ? css`
        width: 100%;
        height: 100%;
        border-radius: 0;
        display: flex;
        flex-direction: column;
      ` : css`
        max-height: 90vh;
        border-radius: 16px;
        ${getWidthClass(props.width)}

        ${(props.position === 'bottom-left' || props.position === 'bottom-right') && css`
          @media (min-width: 768px) {
            margin-bottom: 1rem;
            ${props.position === 'bottom-left' ? 'margin-left: 1rem;' : 'margin-right: 1rem;'}
            max-height: calc(100vh - 2rem);
          }
        `}
      `}
`;
const getWidthClass = (width)=>{
    switch(width){
        case 'sm':
            return css`max-width: 36rem;`;
        case 'md':
            return css`max-width: 48rem;`;
        case 'lg':
            return css`max-width: 60rem;`;
        case 'xl':
            return css`max-width: 72rem;`;
        case 'xxl':
            return css`max-width: 90rem;`;
        case 'full':
            return css`
        max-width: 100%;
        margin-left: 1rem;
        margin-right: 1rem;
      `;
        default:
            return css`max-width: 48rem;`;
    }
};
const ModalHeader = styled.div`
  position: sticky;
  top: 0;
  display: flex;
  align-items: center;
  justify-content: space-between;
  border-bottom: 1px solid var(--theme-border);
  padding: 0.75rem 1rem;
  /* 배경색: 기본은 my-post-background, 다크/vscode 모드에서는 my-card-background 사용 */
  background-color: var(--my-post-background);
  html.dark &, body.vscode-dark & {
      background-color: var(--my-card-background);
  }
  z-index: 10;
  cursor: ${(props)=>props.isDraggable ? 'move' : 'default'};
  min-height: 50px;

  // --- Responsive Styles for Header ---
  @media (max-width: ${(props)=>props.theme.breakpoints.sm}) {
    padding: 0.5rem 0.75rem; // Reduced padding for smaller screens
    min-height: 40px; // Reduced min-height
  }
`;
const ModalTitle = styled.h3`
  /* typography 값은 theme prop 유지 */
  font-weight: ${(props)=>props.theme.typography.fontWeight.semibold};
  font-size: ${(props)=>props.theme.typography.fontSize.lg};
  color: var(--theme-text);
  margin: 0;
  text-align: center;

  // --- Responsive Styles for Title ---
  @media (max-width: ${(props)=>props.theme.breakpoints.sm}) {
    font-size: ${(props)=>props.theme.typography.fontSize.base}; // Reduced font size for smaller screens
  }
`;
const HeaderSection = styled.div`
  display: flex;
  align-items: center;
  flex: 1;

  &:first-of-type {
    justify-content: flex-start;
  }
  &:last-of-type {
    justify-content: flex-end;
  }
  ${(props)=>props.center && css`
    justify-content: center;
    flex-direction: column;
    align-items: center;
    gap: 0.25rem;
  `}
`;
const OpacitySliderContainer = styled.div`
  display: flex;
  align-items: center;
  gap: 0.5rem;
  width: 150px;

  input[type="range"] {
    flex-grow: 1;
    cursor: pointer;
    height: 4px;
    accent-color: var(--theme-primary);
`;
const ModalBody = styled.div`
/* Apply padding conditionally based on noPadding prop */
padding: ${(props)=>props.noPadding ? '0' : '1rem'};

  ${(props)=>props.isFullscreenMobile && css`
    flex: 1;
    overflow-y: auto;
    /* Ensure no padding conflicts in fullscreen mobile if needed */
    padding: ${props.noPadding ? '0' : '1rem'};
  `}
`;
